import { tzDate } from "@mgdx-libs/dayjs";
import { useStore } from "zustand";
import { createStore } from "zustand/vanilla";

import {
  ClinicReservation,
  IntegratedCounselingState,
  MynaVerificationStatus,
  PharmacyReservation,
  SaveTemporaryDataPayload,
} from "./integratedCounseling/types";
import { persistStore } from "./persistStore";

export type IntegratedCounselingActions = {
  updateClinicReservation: (data: Partial<ClinicReservation>) => void;
  updatePharmacyReservation: (data: Partial<PharmacyReservation>) => void;
  clearClinicReservation: () => void;
  clearPharmacyReservation: () => void;
  saveTemporaryData: (payload: SaveTemporaryDataPayload) => void;
  resetTemporaryData: () => void;
  // 医療機関・診療科目・来院区分のみ保持してリセット
  resetIntegratedCounselingKeepClinicSelection: () => void;
  resetIntegratedCounseling: () => void;
  setMynaVerificationStatus: (status: MynaVerificationStatus) => void;
  setMynaVerificationToken: (token: string) => void;
  setMynaAuthState: (authState: string) => void;
  setPharmacyMynaAuthState: (authState: string) => void;
  setPharmacyMynaVerificationStatus: (status: MynaVerificationStatus) => void;
};

const initialTemporaryData: IntegratedCounselingState["temporaryData"] = {
  currentStep: 0,
  completedSteps: [],
  lastSavedAt: null,
};

export const initialIntegratedCounselingState: IntegratedCounselingState = {
  job: {
    id: null,
    status: "idle",
    clinicCounselingId: null,
    pharmacyCounselingId: null,
    failedReason: null,
    createdAt: null,
    updatedAt: null,
  },
  clinicReservation: {},
  pharmacyReservation: {},
  temporaryData: initialTemporaryData,
};

export const integratedCounselingStore = createStore<
  IntegratedCounselingState & { actions: IntegratedCounselingActions }
>()((set) => ({
  ...initialIntegratedCounselingState,
  actions: {
    updateClinicReservation: (data) =>
      set((state) => ({
        clinicReservation: { ...state.clinicReservation, ...data },
        temporaryData: { ...state.temporaryData, lastSavedAt: tzDate().toISOString() },
      })),
    updatePharmacyReservation: (data) =>
      set((state) => ({
        pharmacyReservation: { ...state.pharmacyReservation, ...data },
        temporaryData: { ...state.temporaryData, lastSavedAt: tzDate().toISOString() },
      })),
    clearClinicReservation: () => set({ clinicReservation: {} }),
    clearPharmacyReservation: () => set({ pharmacyReservation: {} }),
    saveTemporaryData: ({ currentStep, completedStep }) =>
      set(({ temporaryData }) => ({
        temporaryData: {
          currentStep: currentStep ?? temporaryData.currentStep,
          completedSteps:
            completedStep !== undefined && !temporaryData.completedSteps.includes(completedStep)
              ? [...temporaryData.completedSteps, completedStep]
              : temporaryData.completedSteps,
          lastSavedAt: tzDate().toISOString(),
        },
      })),
    resetTemporaryData: () => set({ temporaryData: initialTemporaryData }),
    resetIntegratedCounselingKeepClinicSelection: () =>
      set(({ clinicReservation: { clinicId, departmentId, hasVisitedBefore } }) => ({
        ...initialIntegratedCounselingState,
        clinicReservation: {
          ...(clinicId !== undefined && { clinicId }),
          ...(departmentId !== undefined && { departmentId }),
          ...(hasVisitedBefore !== undefined && { hasVisitedBefore }),
        },
      })),
    resetIntegratedCounseling: () => set(initialIntegratedCounselingState),
    setMynaVerificationStatus: (mynaVerificationStatus) =>
      set((state) => ({ clinicReservation: { ...state.clinicReservation, mynaVerificationStatus } })),
    setMynaVerificationToken: (mynaVerificationToken) =>
      set((state) => ({ clinicReservation: { ...state.clinicReservation, mynaVerificationToken } })),
    setMynaAuthState: (mynaAuthState) =>
      set((state) => ({ clinicReservation: { ...state.clinicReservation, mynaAuthState } })),
    setPharmacyMynaAuthState: (mynaAuthState) =>
      set((state) => ({ pharmacyReservation: { ...state.pharmacyReservation, mynaAuthState } })),
    setPharmacyMynaVerificationStatus: (mynaVerificationStatus) =>
      set((state) => ({ pharmacyReservation: { ...state.pharmacyReservation, mynaVerificationStatus } })),
  },
}));

// 入力が 10 分間ないと最初からやり直す
export const INTEGRATED_COUNSELING_TIMEOUT_MS = 10 * 60 * 1000;

// localStorage に保存し、ドメイン外へ遷移して戻っても入力を引き継ぐ。
// 画像や住所を含むため、画面を開かないまま期限を過ぎた入力も次の起動で消す
persistStore(
  integratedCounselingStore,
  {
    key: "persist:integratedCounseling",
    legacyReduxPersistVersion: -1,
    legacyReduxPersistSavedAt: (state) => {
      const savedAt = state.temporaryData.lastSavedAt;
      if (typeof savedAt !== "string") return undefined;
      const time = Date.parse(savedAt);
      return Number.isFinite(time) ? time : undefined;
    },
    fields: ["job", "clinicReservation", "pharmacyReservation", "temporaryData"],
    maxAgeMs: INTEGRATED_COUNSELING_TIMEOUT_MS,
  },
  initialIntegratedCounselingState
);

export const useIntegratedCounselingStore = <T>(
  selector: (state: IntegratedCounselingState & { actions: IntegratedCounselingActions }) => T
) => useStore(integratedCounselingStore, selector);

export const useIntegratedCounselingActions = () => useIntegratedCounselingStore((state) => state.actions);
